import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionButtons,
  Badge,
  Button,
  DataTable,
  EmptyState,
  type ActionButtonConfig,
  type DataTableColumn,
} from '@enamelai/design-system'
import { Icon as ChevronLeftIcon } from '@enamelai/design-system/icons/Chevron_left'
import { Icon as DownloadIcon } from '@enamelai/design-system/icons/Download'
import { Icon as FileIcon } from '@enamelai/design-system/icons/File_2'
import { Icon as RefreshIcon } from '@enamelai/design-system/icons/Refresh_CW'
import { Icon as ViewIcon } from '@enamelai/design-system/icons/View'
import { useFormsTableState } from '@components/pages/FormBuilder/Shared/useFormsTableState'
import FormPreview from '@components/pages/FormBuilder/FormContentArea/FormPreview.component'
import { FormPdfCapture } from '@components/pages/FormBuilder/Shared/FormPdfCapture'
import '@components/pages/FormBuilder/FormContentArea/FormBuilderContent.css'
import type { PatientFormDocumentView } from './mockData'
import { generateFormPDF } from '@utils/formPdfGenerator.util'
import {
  getCompletedFormDownloadUrl,
  regenerateSignedFormPdf,
} from '@services/api/forms.api'
import { useAppSelector } from '@store/hooks'
import { buildClinicPdfHeaderPayload } from '@utils/clinic.util'
import './PatientFormsViewMainContent.css'

const formatDate = (value: string): string =>
  value
    ? new Date(value).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      })
    : '—'

const toTimestamp = (value: string): number => {
  const time = value ? new Date(value).getTime() : Number.NaN
  return Number.isNaN(time) ? 0 : time
}

type DocumentRow = {
  id: string
  title: string
  dateSent: string
  dateCompleted: string
  completed: boolean
}

const getSortValue = (row: DocumentRow, columnId: string): string | number => {
  if (columnId === 'dateSent') return toTimestamp(row.dateSent)
  if (columnId === 'dateCompleted') return toTimestamp(row.dateCompleted)
  return row.title
}

type PatientFormsViewMainContentProps = {
  documents: PatientFormDocumentView[]
  viewingDocument: PatientFormDocumentView | null
  patientName: string
  onViewDocument: (doc: PatientFormDocumentView | null) => void
  onClosePreview: () => void
  onPdfRegenerationStarted?: (assignmentId: string) => void
}

const PatientFormsViewMainContent: React.FC<
  PatientFormsViewMainContentProps
> = ({
  documents,
  viewingDocument,
  patientName,
  onViewDocument,
  onClosePreview,
  onPdfRegenerationStarted,
}) => {
  const [downloadingDocument, setDownloadingDocument] =
    useState<PatientFormDocumentView | null>(null)
  const [pdfNotice, setPdfNotice] = useState('')
  const [statusOverrides, setStatusOverrides] = useState<
    Record<string, string>
  >({})
  const downloadContainerRef = useRef<HTMLDivElement>(null)
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const clinicPdfHeader = useMemo(
    () => buildClinicPdfHeaderPayload(selectedClinic),
    [selectedClinic],
  )

  const handleView = useCallback(
    (doc: PatientFormDocumentView) => {
      onViewDocument(doc)
    },
    [onViewDocument],
  )

  const handleDownload = useCallback(
    (doc: PatientFormDocumentView) => {
      const status =
        (doc.assignmentId && statusOverrides[doc.assignmentId]) ||
        doc.signedPdfStatus
      if (status === 'pending') {
        setPdfNotice('Signed PDF processing')
        return
      }
      if (status === 'failed') {
        setPdfNotice(
          doc.signedPdfLastError ||
            'Signed PDF generation failed. Regenerate the PDF.',
        )
        return
      }
      if (doc.assignmentId && doc.signedPdfBlobName) {
        getCompletedFormDownloadUrl(doc.assignmentId)
          .then((res) => {
            if (res.download_url) {
              window.open(res.download_url, '_blank', 'noopener,noreferrer')
            } else {
              setPdfNotice('Signed PDF is not available yet.')
            }
          })
          .catch(() => {
            setPdfNotice('Signed PDF is not available yet.')
          })
        return
      }
      setDownloadingDocument(doc)
    },
    [statusOverrides],
  )

  const handleRegenerate = useCallback(
    (doc: PatientFormDocumentView) => {
      if (!doc.assignmentId) return
      const assignmentId = doc.assignmentId
      regenerateSignedFormPdf(assignmentId)
        .then(() => {
          setStatusOverrides((prev) => ({
            ...prev,
            [assignmentId]: 'pending',
          }))
          onPdfRegenerationStarted?.(assignmentId)
          setPdfNotice('Signed PDF processing')
        })
        .catch(() => {
          setPdfNotice('Could not start signed PDF generation.')
        })
    },
    [onPdfRegenerationStarted],
  )

  useEffect(() => {
    setStatusOverrides((previous) => {
      const next = { ...previous }
      let changed = false
      for (const document of documents) {
        if (
          document.assignmentId &&
          next[document.assignmentId] === 'pending' &&
          document.signedPdfStatus &&
          document.signedPdfStatus !== 'pending'
        ) {
          delete next[document.assignmentId]
          changed = true
        }
      }
      return changed ? next : previous
    })
  }, [documents])

  useEffect(() => {
    if (!downloadingDocument || downloadingDocument.completed === false) return
    const doc = downloadingDocument
    const timer = setTimeout(() => {
      const el = downloadContainerRef.current
      if (el) {
        const filename = `${patientName.replace(/\s+/g, '-')}-${doc.title.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`
        generateFormPDF({
          element: el,
          filename,
          title: doc.title,
          clinicPdfHeader,
        }).finally(() => setDownloadingDocument(null))
      } else {
        setDownloadingDocument(null)
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [downloadingDocument, clinicPdfHeader, patientName])

  const completedDocuments = useMemo(
    () => documents.filter((doc) => doc.completed !== false),
    [documents],
  )
  const documentById = useMemo(
    () => new Map(completedDocuments.map((doc) => [doc.id, doc])),
    [completedDocuments],
  )
  const documentRows = useMemo(
    (): DocumentRow[] =>
      completedDocuments.map((doc) => ({
        id: doc.id,
        title: doc.title,
        dateSent: doc.dateSent,
        dateCompleted: doc.dateCompleted,
        completed: doc.completed !== false,
      })),
    [completedDocuments],
  )
  const table = useFormsTableState(documentRows, getSortValue)

  const columns: DataTableColumn<DocumentRow>[] = useMemo(
    () => [
      {
        id: 'title',
        label: 'Forms',
        sortable: true,
        renderCell: (row) => (
          <span className="pfv-form-title-cell">
            <FileIcon className="pfv-form-title-icon" aria-hidden />
            {row.title}
          </span>
        ),
      },
      {
        id: 'dateSent',
        label: 'Date Sent',
        sortable: true,
        width: 173,
        renderCell: (row) => formatDate(row.dateSent),
      },
      {
        id: 'dateCompleted',
        label: 'Date Completed',
        sortable: true,
        width: 173,
        renderCell: (row) =>
          row.completed ? formatDate(row.dateCompleted) : 'Incomplete',
      },
    ],
    [],
  )
  const renderActions = useCallback(
    (tableRow: DocumentRow): React.ReactElement | null => {
      const row = documentById.get(tableRow.id)
      if (!row) return null
      const pdfStatus =
        (row.assignmentId ? statusOverrides[row.assignmentId] : undefined) ??
        row.signedPdfStatus
      const actions: ActionButtonConfig[] = [
        {
          id: 'view',
          icon: <ViewIcon className="ds-action-button__svg" aria-hidden />,
          label: `View ${row.title}`,
          tone: 'primary',
          variant: 'solid',
          onClick: () => handleView(row),
        },
      ]
      if (pdfStatus === 'failed') {
        actions.push({
          id: 'retry',
          icon: <RefreshIcon className="ds-action-button__svg" aria-hidden />,
          label: `Retry PDF generation for ${row.title}`,
          tone: 'primary',
          variant: 'outline',
          onClick: () => handleRegenerate(row),
        })
      } else if (pdfStatus !== 'pending') {
        actions.push({
          id: 'download',
          icon: <DownloadIcon className="ds-action-button__svg" aria-hidden />,
          label:
            row.completed === false ? 'Not available' : `Download ${row.title}`,
          tone: 'primary',
          variant: 'outline',
          disabled: row.completed === false,
          onClick: () => handleDownload(row),
        })
      }
      return (
        <div className="pfv-row-actions">
          <ActionButtons actions={actions} />
          {pdfStatus === 'pending' ? (
            <Badge
              mode="tinted"
              color="blue"
              className="pfv-pdf-processing-badge"
            >
              <span role="status" aria-live="polite">
                Processing PDF
              </span>
            </Badge>
          ) : null}
        </div>
      )
    },
    [
      documentById,
      handleView,
      handleDownload,
      handleRegenerate,
      statusOverrides,
    ],
  )

  const getPreviewProps = useCallback((doc: PatientFormDocumentView) => {
    return {
      groups: doc.groups,
      formTitle: doc.title,
      fieldValues: doc.fieldValues,
    }
  }, [])

  if (viewingDocument) {
    const previewProps = getPreviewProps(viewingDocument)
    return (
      <>
        <div className="pfv-main">
          <div className="pfv-preview">
            <div className="pfv-preview-header">
              <Button
                variant="ghost"
                size="md"
                type="button"
                className="pfv-preview-back"
                onClick={onClosePreview}
                aria-label="Back to list"
              >
                <ChevronLeftIcon aria-hidden />
              </Button>
            </div>
            <div className="pfv-preview-body">
              <div
                className="form-preview-container"
                id="form-preview-container"
              >
                <FormPreview
                  groups={previewProps.groups}
                  formTitle={previewProps.formTitle}
                  fieldValues={previewProps.fieldValues}
                  patientName={patientName}
                  readOnlyDisplayMode="print"
                />
              </div>
            </div>
          </div>
        </div>
        {downloadingDocument && downloadingDocument.completed !== false && (
          <div
            ref={downloadContainerRef}
            style={{
              position: 'fixed',
              left: '-9999px',
              top: 0,
              width: '794px',
              zIndex: -1,
              pointerEvents: 'none',
            }}
            className="form-preview-container"
            aria-hidden="true"
          >
            <FormPdfCapture
              groups={downloadingDocument.groups}
              formTitle={downloadingDocument.title}
              fieldValues={downloadingDocument.fieldValues}
              patientName={patientName}
              readOnlyDisplayMode="print"
            />
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <div className="pfv-main">
        {pdfNotice ? <div className="pfv-main-notice">{pdfNotice}</div> : null}
        <div className="pfv-main-body">
          {completedDocuments.length === 0 ? (
            <EmptyState
              className="pfv-empty-state"
              title="No forms or documents yet"
              description="Select an appointment from the Pending Forms tab to view completed forms."
              outlined
            />
          ) : (
            <div className="pfv-table-wrapper">
              <DataTable<DocumentRow>
                className="pfv-table"
                columns={columns}
                rows={table.pageRows}
                sortColumnId={table.sortColumnId}
                sortDirection={table.sortDirection}
                onSort={table.onSort}
                pagination={table.pagination}
                showActions
                actionsLabel="Action"
                renderActions={renderActions}
              />
            </div>
          )}
        </div>
      </div>
      {downloadingDocument && downloadingDocument.completed !== false && (
        <div
          ref={downloadContainerRef}
          style={{
            position: 'fixed',
            left: '-9999px',
            top: 0,
            width: '794px',
            zIndex: -1,
            pointerEvents: 'none',
          }}
          className="form-preview-container"
          aria-hidden="true"
        >
          <FormPdfCapture
            groups={downloadingDocument.groups}
            formTitle={downloadingDocument.title}
            fieldValues={downloadingDocument.fieldValues}
            patientName={patientName}
            readOnlyDisplayMode="print"
          />
        </div>
      )}
    </>
  )
}

export default PatientFormsViewMainContent
